import React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';

import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import Switch from "@material-ui/core/Switch";

import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Legend, Tooltip as RechartsTooltip,
} from 'recharts';

import FilterListView from '../../components/FilterListView';
import Scenario from '../../api/Scenario';
import Header from '../../components/utils/Header';
import Collapsible from "../../components/utils/Collapsible";
import {
  formatRatioPercent,
  formatVolume, unitFormatter,
} from '../../utils';
import ConditionalFormat from '../../components/utils/ConditionalFormat';
import ScenarioSelect from '../scenarios/ScenarioSelect';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import DataFrameDownload from '../../components/utils/DataFrameDownload';


const styles = theme => ({
  chartContainer: {
    overflow: 'hidden',
  },
  tablePaper: {
    overflow: 'auto',
  },
  tableSeparator: {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
  subtotalColor: {
    backgroundColor: theme.palette.grey[100],
  },
  totalColor: {
    backgroundColor: theme.palette.grey[300],
  },
  autoRowSize: {
    tableLayout: 'auto',
    width: 'auto',
  },
});


@withStyles(styles)
@withRouter
@observer
class SupplyDetailView extends BaseScenarioDashboardView {
  static apiSubPath = '/supply_detail/';

  @observable scenario1 = null;
  @observable scenario2 = null;
  @observable goodsLevel = null;

  _setData(data) {
    this.scenario1 = data.scenario1;
    this.scenario2 = data.scenario2;
    this.goodsLevel = data.goods_level;
  }

  _getQueryParams() {
    return {
      scenario1: this.scenario1,
      scenario2: this.scenario2,
      goods_level: this.goodsLevel,
    };
  }

  onScenario1Change = this.genericChangeHandler('scenario1');
  onScenario2Change = this.genericChangeHandler('scenario2');

  @action onGoodsLevelChange = event => {
    this.goodsLevel = event.target.checked ? 'FG' : 'SFG';
    this.updateData();
  };

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={480} height={55} />;
    }
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          <Typography noWrap>
            SFG
            <Switch
              checked={this.goodsLevel === 'FG'}
              onChange={this.onGoodsLevelChange}
            />
            FG
          </Typography>
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 1"
            scenarioId={this.scenario1}
            choices={this.data.scenario1_choices}
            onScenarioChange={this.onScenario1Change}
          />
        </Grid>
        <Grid item>
          <ScenarioSelect
            label="Scenario 2"
            scenarioId={this.scenario2}
            choices={this.data.scenario2_choices}
            onScenarioChange={this.onScenario2Change}
          />
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/supply_detail/?' + this.getQueryString())}
            permission="athena.download_supply_detail"
          />
        </Grid>
      </Grid>
    );
  }

  getChart(data, unit, numDecimals = 0) {
    const formatter = unitFormatter(unit, numDecimals);
    return (
      <ResponsiveContainer aspect={21 / 9}>
        <ComposedChart data={[...data]} margin={{ top: 16, left: 30 }}>
          <XAxis dataKey="month" stroke="#cdcdcd" />
          <YAxis tickFormatter={formatter} orientation="left" domain={['auto', 'auto']} stroke="#cdcdcd" />
          <RechartsTooltip />
          <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
          {this.getLine(
            'Scenario 1',
            {
              stroke: '#f5a623', strokeWidth: 4, strokeDasharray: '5 5', strokeOpacity: 0.8, formatter,
            },
          )}
          {
            this.scenario2 === null
              ? null
              : this.getLine(
                'Scenario 2',
              { stroke: '#0069b3', strokeWidth: 4, strokeOpacity: 0.8, formatter },
              )
          }
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  getTable(data) {
    const { classes } = this.props;
    const headers = Object.keys(data[0]).filter(key => !key.endsWith(' (%)'));
    const isIndex = key => (key === 'Factory') || key.startsWith('Recipe type');
    const formatValue = (row, key) => {
      const value = row[key];
      if (isIndex(key)) {
        return value;
      }
      const formattedValue = formatVolume(row[key], 't', 0);
      const percentKey = key + ' (%)';
      if (percentKey in row) {
        const ratioPercent = row[percentKey];
        return (
          <ConditionalFormat value={ratioPercent} name="supply-detail">
            {formattedValue} ({formatRatioPercent(ratioPercent)})
          </ConditionalFormat>
        );
      }
      return formattedValue;
    };
    const getCellClass = key => isIndex(key) ? classes.tableSeparator : null;
    const getCellProps = key => ({
      key, variant: isIndex(key) ? 'head' : null, align: isIndex(key) ? 'left' : 'right',
      className: getCellClass(key),
    });
    return (
      <Paper className={classes.tablePaper}>
        <Table stickyHeader size="small" className={classes.autoRowSize}>
          <TableHead>
            <TableRow>
              {headers.map(key => (
                <TableCell {...getCellProps(key)}>{key}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                {headers.map(key => (
                  <TableCell {...getCellProps(key)}>{formatValue(row, key)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    )
  }

  insideCollapsible(table, chart, titleTable, titleGraph, graphUnit, graphDecimals = 0) {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid item className={classes.chartContainer}>
          <Typography variant="h6" gutterBottom>{titleGraph}</Typography>
          {this.getChart(chart, graphUnit, graphDecimals)}
        </Grid>
        <Grid item>
          <Typography variant="h6" gutterBottom>{titleTable}</Typography>
          {this.getTable(table)}
        </Grid>
      </Grid>
    );
  }

  renderContent() {
    if (!this.ready) {
      return <Grid item><Skeleton variant="rect" height={550} /></Grid>;
    }
    return (
      <>
        <Grid item>
          <Collapsible title="Factory">
            {this.insideCollapsible(
              this.data.factory_table, this.data.factory_graph,
              'Production volume', 'Production volume',
              't',
            )}
          </Collapsible>
        </Grid>
        <Grid item>
          <Collapsible title="Recipe">
            {this.insideCollapsible(
              this.data.recipe_table, this.data.recipe_graph,
              'Production volume', 'Average run rate',
              't/h', 2,
            )}
          </Collapsible>
        </Grid>
      </>
    );
  }

  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid item>
          <Grid
            container
            spacing={1}
            justify="space-between"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item>
              <Header title="Supply Detail" />
            </Grid>
            <Grid item>
              {this.controls}
            </Grid>
          </Grid>
        </Grid>
        {this.renderContent()}
      </Grid>
    );
  }
}


export default SupplyDetailView;
